import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import {
  ArrowDown,
  Bell,
  BellOff,
  ChevronUp,
  Download,
  LogOut,
  RotateCcw,
  Search,
  UserRound,
  X,
  MoreHorizontal,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { Button, buttonVariants } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import { cn } from "@/shared/lib/utils";
import { conversationKey, useChatStore } from "@/store/chat";
import {
  useChat,
  useChatActions,
  useLiveChat,
  useMessageSearch,
  useOnline,
  useTranscript,
} from "../api/use-chat";
import { useAssistants } from "../api/use-directory";
import {
  dayLabel,
  downloadTranscript,
  groupsWithPrevious,
  messageDay,
  transcriptText,
} from "../lib/messages";
import type {
  ChatSummary,
  Identity,
  Message,
  PendingMessage,
  Session,
} from "../types";
import { useUnreadBoundary } from "../hooks/use-unread-boundary";
import { useChatViewport } from "../hooks/use-chat-viewport";
import { useTypingIndicator } from "../hooks/use-typing-indicator";
import { ConversationDetails } from "./conversation-details";
import { Composer } from "./composer";
import { MessageItem } from "./message-item";
import { WidgetFrame } from "./widget-frame";
import { assistantName } from "../lib/assistant-name";
import { allows } from "../lib/capabilities";
import { ChatStatus } from "./chat-status";
import {
  ERROR_CLASS,
  HEADER_ICON_CLASS,
  HOVER_MUTED,
  HOVER_MUTED_TEXT,
} from "./styles";

const EMPTY_PENDING: PendingMessage[] = [];

const TOOL_ITEM_CLASS =
  "min-h-11 w-full cursor-pointer justify-start px-2 text-xs font-normal aria-pressed:bg-secondary aria-pressed:text-gold motion-reduce:transition-none";

const ICON_BUTTON_CLASS = cn(
  "size-11 min-h-11 min-w-11 shrink-0 cursor-pointer rounded-full text-muted-foreground",
  HOVER_MUTED_TEXT,
);

const NOTE_CLASS =
  "p-3 text-center text-xs leading-[1.7] text-muted-foreground";

const WORKING_CLASS =
  "my-2 flex w-fit items-center gap-1.5 rounded-2xl rounded-bl-sm border border-gold/20 bg-secondary px-4 py-3";

const WORKING_DOT_CLASS =
  "size-1.5 animate-pulse rounded-full bg-gold-soft motion-reduce:animate-none";

const PAGE_BUTTON_CLASS =
  "mx-auto my-2 flex min-h-11 cursor-pointer px-3 text-xs text-gold-soft motion-reduce:transition-none";

const PENDING_ACTION_CLASS = cn(
  "min-h-11 cursor-pointer px-2 text-xs",
  HOVER_MUTED,
);

export function Conversation({
  identity,
  chatId,
  session,
  onLogout,
  loggingOut,
  totalUnread,
  summary,
  onBack,
}: {
  identity: Identity;
  chatId: string;
  session: Session;
  onLogout: () => void;
  loggingOut: boolean;
  totalUnread: number;
  summary?: ChatSummary;
  onBack: () => void;
}) {
  const query = useChat(identity, chatId);
  const actions = useChatActions(identity, chatId);
  const { connection, typingMemberIds, sendTyping } = useLiveChat(
    identity,
    chatId,
    session.mode !== "ui-mock",
  );
  const online = useOnline();
  const open = useChatStore((state) => state.open);
  const expanded = useChatStore((state) => state.expanded);
  const setExpanded = useChatStore((state) => state.setExpanded);
  const onTyping = useTypingIndicator(
    sendTyping,
    open && online && connection === "Connected",
  );
  const [rosterOpen, setRosterOpen] = useState(false);
  const storageKey = conversationKey(identity, chatId);
  const pending = useChatStore(
    (state) => state.pending[storageKey] ?? EMPTY_PENDING,
  );
  const putPending = useChatStore((state) => state.putPending);
  const removePending = useChatStore((state) => state.removePending);
  const [error, setError] = useState("");
  const [reply, setReply] = useState<Message | null>(null);
  const [editing, setEditing] = useState<Message | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchId = useId();
  const exportHistory = useTranscript(chatId);
  const searching = searchOpen && Boolean(searchTerm.trim());
  const search = useMessageSearch(identity, chatId, searchTerm, searching);
  const data = query.data;
  const messages = data?.messages ?? [];
  const title = data?.title ?? summary?.title ?? "Conversation";
  const participants = data?.participants ?? summary?.participants ?? [];
  const assistants = useAssistants(identity).data?.assistants;
  const name = assistantName(participants, assistants, session);
  const typingNames = participants
    .filter(
      (participant) =>
        participant.memberId !== identity.memberId &&
        typingMemberIds.includes(participant.memberId),
    )
    .map((participant) =>
      participant.role === "ASSISTANT" ? name : participant.name,
    );
  const unread = useUnreadBoundary(data, open, identity.memberId);
  const unreadMarker = useRef<HTMLDivElement>(null);
  const loadingOlder = useRef(false);
  const automaticBefore = useRef<string | null>(null);
  const searchMessages =
    search.data?.pages.flatMap((page) => page.messages) ?? [];
  const visibleMessages = searching ? searchMessages : messages;
  const unseenPending = pending.filter(
    (item) =>
      !messages.some(
        (message) =>
          message.memberId === identity.memberId &&
          message.reqKey === item.reqKey,
      ),
  );
  const { mutate: savePreferences } = actions.preferences;
  const saveDraft = useCallback(
    (draft: string) => savePreferences({ draft }),
    [savePreferences],
  );
  const { mutate: acknowledge, isPending: receiptPending } = actions.receipts;
  const {
    atLatest,
    setAtLatest,
    transcript,
    bottom,
    jumpToLatest,
    jumping,
    scrollRevision,
  } = useChatViewport({
    data,
    dataUpdatedAt: query.dataUpdatedAt,
    memberId: identity.memberId,
    open,
    online,
    searching,
    pendingCount: unseenPending.length,
    receiptPending,
    acknowledge,
    boundaryReady: unread.ready,
  });
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);
  useEffect(() => {
    for (const item of pending) {
      if (
        data?.messages.some(
          (message) =>
            message.memberId === identity.memberId &&
            message.reqKey === item.reqKey,
        )
      )
        removePending(storageKey, item.reqKey);
    }
  }, [data, pending, identity.memberId, storageKey, removePending]);

  const send = async (item: PendingMessage) => {
    setError("");
    putPending(storageKey, { ...item, state: "sending", error: undefined });
    setAtLatest(true);
    try {
      await actions.send.mutateAsync(item);
      removePending(storageKey, item.reqKey);
    } catch (problem) {
      const message =
        problem instanceof Error
          ? problem.message
          : "Message could not be confirmed.";
      putPending(storageKey, { ...item, state: "failed", error: message });
    }
  };
  const loadOlder = async (automatic = false) => {
    const before = data?.before;
    if (
      !before ||
      searching ||
      jumping.current ||
      loadingOlder.current ||
      actions.older.isPending
    )
      return;
    // A failed automatic page keeps its explicit retry button. Repeated scroll
    // events cannot issue duplicate requests while the cursor is unchanged.
    if (automatic && automaticBefore.current === before) return;
    automaticBefore.current = before;
    loadingOlder.current = true;
    const revision = scrollRevision.current;
    const height = transcript.current?.scrollHeight ?? 0;
    const top = transcript.current?.scrollTop ?? 0;
    setAtLatest(false);
    try {
      await actions.older.mutateAsync(before);
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => {
          if (transcript.current && revision === scrollRevision.current)
            transcript.current.scrollTop =
              top + transcript.current.scrollHeight - height;
          resolve();
        }),
      );
    } catch (problem) {
      setError(
        problem instanceof Error ? problem.message : "Unable to load history.",
      );
    } finally {
      loadingOlder.current = false;
    }
  };
  const edit = async (message: Message, text: string, mentions: string[]) => {
    await actions.message.mutateAsync({
      action: "edit",
      rootId: message.rootId,
      reqKey: crypto.randomUUID(),
      text,
      mentions,
      expectedEventNo: message.eventNo,
    });
    setEditing(null);
  };
  const exportChat = async () => {
    try {
      downloadTranscript(
        transcriptText(await exportHistory.mutateAsync(), identity.memberId),
      );
    } catch (problem) {
      setError(
        problem instanceof Error
          ? problem.message
          : "Unable to export conversation.",
      );
    }
  };
  const cancelContext = () => {
    setReply(null);
    setEditing(null);
  };
  const mutationError =
    actions.message.error ??
    actions.preferences.error ??
    actions.handoff.error ??
    actions.receipts.error;
  return (
    <WidgetFrame
      identityName={identity.name}
      demo={session.mode === "local-demo" || session.mode === "ui-mock"}
      unread={totalUnread}
      title={title}
      ai={
        data?.kind !== "G" &&
        participants.some((participant) => participant.role === "ASSISTANT")
      }
      subtitle={
        data?.kind === "G"
          ? participants
              .filter(
                (participant) => participant.memberId !== identity.memberId,
              )
              .map((participant) => participant.name)
              .join(", ")
          : participants.some((participant) => participant.role === "ASSISTANT")
            ? "Your handler"
            : "Direct message"
      }
      onBack={onBack}
      onTitleClick={() => setRosterOpen(!rosterOpen)}
      rosterOpen={rosterOpen}
      headerActions={
        <details
          className="relative shrink-0"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              event.currentTarget.open = false;
              event.currentTarget.querySelector("summary")?.focus();
            }
          }}
        >
          <summary
            aria-label="Conversation tools"
            title="Conversation tools"
            className={cn(
              buttonVariants({ variant: "ghost", size: "icon" }),
              HEADER_ICON_CLASS,
              "list-none [&::-webkit-details-marker]:hidden",
            )}
          >
            <MoreHorizontal />
          </summary>
          <div
            className="absolute top-12 right-0 z-[8] grid w-[225px] rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-[0_10px_25px_rgb(0_0_0/45%)]"
            aria-label="Conversation tools"
          >
            <Button
              variant="ghost"
              size="sm"
              aria-label={expanded ? "Restore panel" : "Expand panel"}
              className={TOOL_ITEM_CLASS}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <Minimize2 /> : <Maximize2 />}
              <span>{expanded ? "Restore panel" : "Expand panel"}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Search messages"
              aria-pressed={searchOpen}
              className={TOOL_ITEM_CLASS}
              onClick={() => {
                setSearchOpen(!searchOpen);
              }}
            >
              <Search />
              <span>Search messages</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={
                data?.muted ? "Unmute notifications" : "Mute notifications"
              }
              className={TOOL_ITEM_CLASS}
              disabled={!data || actions.preferences.isPending}
              onClick={() =>
                actions.preferences.mutate({ muted: !data?.muted })
              }
            >
              {data?.muted ? <BellOff /> : <Bell />}
              <span>
                {data?.muted ? "Unmute notifications" : "Mute notifications"}
              </span>
            </Button>
            {data?.canRequestHuman &&
              (data.handoffState === "AI" ||
                data.handoffState === "REQUESTED") && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={
                    data.handoffState === "REQUESTED"
                      ? "Cancel request"
                      : "Ask for a person"
                  }
                  className={TOOL_ITEM_CLASS}
                  disabled={actions.handoff.isPending}
                  onClick={() =>
                    actions.handoff.mutate(
                      data.handoffState === "REQUESTED" ? "AI" : "REQUESTED",
                    )
                  }
                >
                  {data.handoffState === "REQUESTED" ? <X /> : <UserRound />}
                  <span>
                    {data.handoffState === "REQUESTED"
                      ? "Cancel request"
                      : "Ask for a person"}
                  </span>
                </Button>
              )}
            <Button
              variant="ghost"
              size="sm"
              aria-label="Export full transcript"
              className={TOOL_ITEM_CLASS}
              disabled={exportHistory.isPending || !data}
              onClick={() => void exportChat()}
            >
              <Download />
              <span>Export full transcript</span>
            </Button>
            {session.mode !== "glow-session" && (
              <Button
                variant="ghost"
                size="sm"
                aria-label="Sign out of local chat"
                className={TOOL_ITEM_CLASS}
                disabled={loggingOut}
                onClick={onLogout}
              >
                <LogOut />
                <span>Sign out of local chat</span>
              </Button>
            )}
          </div>
        </details>
      }
    >
      {searchOpen && (
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
          <Search
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <Label htmlFor={searchId} className="sr-only">
            Search conversation
          </Label>
          <Input
            id={searchId}
            placeholder="Search conversation…"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            className="h-8 min-w-0 border-transparent bg-transparent px-1 shadow-none dark:bg-transparent"
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close search"
            className={ICON_BUTTON_CLASS}
            onClick={() => {
              setSearchOpen(false);
              setSearchText("");
            }}
          >
            <X />
          </Button>
        </div>
      )}
      <ConversationDetails
        participants={participants}
        memberId={identity.memberId}
        rosterOpen={rosterOpen}
        briefs={
          data?.missionBriefs ?? (data?.missionBrief ? [data.missionBrief] : [])
        }
      />
      <div
        ref={transcript}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-3 outline-none [scrollbar-color:var(--border)_transparent] [scrollbar-width:thin] focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset"
        role="log"
        aria-label={searching ? "Search results" : "Messages"}
        aria-live="off"
        tabIndex={0}
        onScroll={(event) => {
          if (jumping.current) return;
          const node = event.currentTarget;
          setAtLatest(
            node.scrollHeight - node.scrollTop - node.clientHeight < 50,
          );
          if (open && online && !searching && node.scrollTop <= 40)
            void loadOlder(true);
        }}
      >
        {!searching && data?.before && (
          <Button
            variant="ghost"
            size="sm"
            className={PAGE_BUTTON_CLASS}
            disabled={actions.older.isPending}
            onClick={() => void loadOlder()}
          >
            <ChevronUp className="size-3.5" />
            {actions.older.isPending ? "Loading…" : "Earlier messages"}
          </Button>
        )}
        {query.isPending && (
          <p className={NOTE_CLASS}>Opening your conversation…</p>
        )}
        {!query.isPending && !searching && !messages.length && (
          <div className="px-4 py-8 text-center">
            <span className="font-mono text-xs tracking-[0.14em] text-gold-soft">
              THE LINE IS OPEN
            </span>
            <h3 className="my-3 font-serif text-2xl">Start with a hello.</h3>
            <p className="text-sm text-muted-foreground">
              A question, an update, or a small win.
              <br />
              Send a message to start this conversation.
            </p>
          </div>
        )}
        {searching && search.isPending && (
          <p className={NOTE_CLASS}>Searching…</p>
        )}
        {searching && !search.isPending && !visibleMessages.length && (
          <p className={NOTE_CLASS}>No messages match your search.</p>
        )}
        {visibleMessages.map((message, index) => (
          <Fragment key={message.rootId}>
            {(index === 0 ||
              messageDay(message) !==
                messageDay(visibleMessages[index - 1])) && (
              <div
                className="mt-4 mb-2 flex justify-center font-mono text-xs tracking-[0.05em] text-muted-foreground uppercase"
                role="separator"
                aria-label={dayLabel(message)}
              >
                {dayLabel(message)}
              </div>
            )}
            {!searching &&
              unread.count > 0 &&
              message.rootId === unread.firstRoot && (
                <div
                  ref={unreadMarker}
                  className="mx-3 mt-2 mb-4 flex items-center gap-2.5 text-xs text-gold"
                  role="separator"
                  aria-label={`${unread.count} new messages`}
                >
                  <Separator className="flex-1" />
                  <span>
                    {unread.count} new{" "}
                    {unread.count === 1 ? "message" : "messages"}
                  </span>
                  <Separator className="flex-1" />
                </div>
              )}
            <MessageItem
              grouped={
                !searching &&
                message.rootId !== unread.firstRoot &&
                groupsWithPrevious(message, visibleMessages[index - 1])
              }
              mentioned={(message.mentions ?? []).some(
                (mention) => mention.memberId === identity.memberId,
              )}
              message={message}
              own={message.memberId === identity.memberId}
              snapshot={data}
              busy={actions.message.isPending}
              onError={setError}
              onReply={(item) => {
                setReply(item);
                setEditing(null);
              }}
              onEdit={(item) => {
                setEditing(item);
                setReply(null);
              }}
              onDelete={(item) =>
                actions.message.mutate({
                  action: "delete",
                  rootId: item.rootId,
                  expectedEventNo: item.eventNo,
                  reqKey: crypto.randomUUID(),
                })
              }
              onReact={(item, emoji) =>
                actions.message.mutate({
                  action: "reaction",
                  rootId: item.rootId,
                  emoji,
                  active: !item.reactions.some(
                    (reaction) => reaction.emoji === emoji && reaction.mine,
                  ),
                })
              }
            />
          </Fragment>
        ))}
        {searching && search.hasNextPage && (
          <Button
            variant="ghost"
            size="sm"
            className={PAGE_BUTTON_CLASS}
            disabled={search.isFetchingNextPage}
            onClick={() => void search.fetchNextPage()}
          >
            More results
          </Button>
        )}
        {!searching &&
          unseenPending.map((item) => (
            <article
              key={item.reqKey}
              className="relative mt-3 flex flex-col items-end px-3"
            >
              <div className="max-w-[85%] min-w-0 rounded-2xl rounded-br-[6px] bg-primary px-3 py-2 text-base leading-normal wrap-anywhere text-primary-foreground opacity-70">
                {item.text || "Media message"}
              </div>
              <small className="max-w-[85%] text-xs text-muted-foreground">
                {item.state === "sending" ? (
                  "Sending…"
                ) : (
                  <>
                    <span className="mt-1 block">
                      {item.error || "Delivery was not confirmed."}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={PENDING_ACTION_CLASS}
                      disabled={!online}
                      onClick={() => void send(item)}
                    >
                      <RotateCcw className="size-[13px]" />
                      Retry message
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={PENDING_ACTION_CLASS}
                      onClick={() => removePending(storageKey, item.reqKey)}
                    >
                      Discard pending message
                    </Button>
                  </>
                )}
              </small>
            </article>
          ))}
        {!searching &&
          (typingNames.length > 0 ||
            data?.workStatus === "QUEUED" ||
            data?.workStatus === "WORKING") && (
            <div className={WORKING_CLASS} aria-hidden="true">
              {[0, 150, 300].map((delay) => (
                <span
                  key={delay}
                  className={WORKING_DOT_CLASS}
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
            </div>
          )}
        {data?.workStatus === "FAILED" && (
          <p className={NOTE_CLASS}>
            Your message is saved. {name} could not finish this reply.
          </p>
        )}
        {data?.handoffState === "REQUESTED" && (
          <p className={NOTE_CLASS}>
            A person has been requested. {name} is paused until someone takes
            over.
          </p>
        )}
        {data?.handoffState === "HUMAN" && (
          <p className={NOTE_CLASS}>
            A staff member has taken over this conversation.
          </p>
        )}
        <div ref={bottom} className="h-0.5 w-full" />
      </div>
      {(!atLatest || data?.historyWindow) && !searching && (
        <Button
          variant="outline"
          size="sm"
          className="mb-2 min-h-11 cursor-pointer self-center bg-card px-3 text-xs font-normal text-gold motion-reduce:transition-none dark:bg-card"
          disabled={actions.latest.isPending}
          onClick={async () => {
            try {
              if (data?.historyWindow) await actions.latest.mutateAsync();
              jumpToLatest();
            } catch (problem) {
              setError(
                problem instanceof Error
                  ? problem.message
                  : "Unable to load latest messages.",
              );
            }
          }}
        >
          <ArrowDown className="size-3.5" />
          {data?.historyWindow
            ? "Return to latest messages"
            : data?.unreadCount
              ? `${data.unreadCount} new messages · Jump to latest`
              : "Jump to latest"}
        </Button>
      )}
      {(error || query.error || mutationError || search.error) && (
        <div className={ERROR_CLASS} role="alert">
          <span className="flex-1">
            {error ||
              query.error?.message ||
              mutationError?.message ||
              search.error?.message}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className={ICON_BUTTON_CLASS}
            aria-label="Retry conversation sync"
            onClick={() => {
              setError("");
              actions.message.reset();
              actions.preferences.reset();
              actions.handoff.reset();
              actions.receipts.reset();
              void query.refetch();
              if (searching) void search.refetch();
            }}
          >
            <RotateCcw className="size-[15px]" />
          </Button>
        </div>
      )}
      {data && (
        <Composer
          chatId={chatId}
          storageKey={storageKey}
          serverDraft={data.draft}
          title={title}
          participants={participants}
          mediaLimit={data.mediaLimitBytes || 20 * 1024 * 1024}
          mediaEnabled={allows(session, "media")}
          disabled={actions.message.isPending}
          open={open}
          reply={reply}
          editing={editing}
          onCancel={cancelContext}
          onSend={(item) => void send(item)}
          onEdit={edit}
          onDraft={saveDraft}
          onError={setError}
          onTyping={onTyping}
        />
      )}
      <ChatStatus
        connection={connection}
        online={online}
        open={open}
        snapshot={data}
        assistants={assistants}
        session={session}
        memberId={identity.memberId}
        searching={searching}
        typing={
          typingNames.length
            ? `${typingNames.join(", ")} ${typingNames.length === 1 ? "is" : "are"} typing`
            : ""
        }
      />
    </WidgetFrame>
  );
}
